import React from "react";
import i18n from "@i18n";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet";
import { compassColors } from "src/utils/styling";
import {
  breakpointDesktopOnly,
  breakpointMobileOnly,
} from "src/design-system/styles/breakpoints";
import blueberriesLargeImage from "./images/blueberries-large.webp";
import blueberriesSmallImage from "./images/blueberries-small.webp";
import typewriterImage from "./images/typewriter.webp";
import calculatorPreviewImage from "./images/calculatorpreview.webp";
import usePreloadImage from "src/hooks/usePreloadImage";
import { FlexFrame, MainColumn } from "src/components/core/BodyLayout";
import { css } from "@emotion/react";
import { fontTitle, headline } from "src/design-system/styles/fonts";
import {
  createForwardParams,
  updateForwardedParams,
} from "src/utils/urlParams";
import { LandingFooter } from "src/components/footer/LandingFooter";

const containerStyles = css`
  background-color: ${compassColors.white};
  border-radius: 8px;
  margin-top: 32px;
  margin-bottom: 32px;
`;

const Spacer = styled.div`
  margin: 46px 84px;

  @media ${breakpointMobileOnly} {
    margin: 24px 16px;
  }
`;

const Footer = styled.footer`
  background-color: #fff;
  margin-top: 60px;
`;

const HeaderContainer = styled.div`
  background-image: url(${blueberriesSmallImage});
  background-repeat: no-repeat;
  background-position: 0 0;
  background-size: cover;
  padding: 32px 24px 27px;

  @media ${breakpointDesktopOnly} {
    background-image: url(${blueberriesLargeImage});
    padding: 43px 39px 39px 125px;
  }
`;

const HeaderTitle = styled.h1`
  ${fontTitle}
  color: #fff;
  font-size: 18px;
  margin-bottom: 16px;

  @media ${breakpointMobileOnly} {
    font-size: 14px;
  }
`;

const HeaderSubtitle = styled.div`
  ${headline}
  color: #fff;
  font-size: 32px;
  font-weight: 400;
  line-height: 48px;

  @media ${breakpointMobileOnly} {
    font-size: 24px;
    line-height: 32px;
  }
`;

function Header() {
  return (
    <HeaderContainer>
      <HeaderTitle>
        {i18n.t("freeProductsCalorieDeficitCalculator:pageHeading")}
      </HeaderTitle>
      <HeaderSubtitle>
        {i18n.t("freeProductsCalorieDeficitCalculator:pageDescription")}
      </HeaderSubtitle>
    </HeaderContainer>
  );
}

export function calorieCalculatorURL() {
  const url = new URL(window.location.href);
  updateForwardedParams({ s: null });
  url.search = createForwardParams().toString();
  url.pathname = "/f/calorie-deficit-calculator/";
  return url.href;
}

export default function Layout({ children, header, footer }) {
  const backgroundSmall = usePreloadImage(blueberriesLargeImage);
  const backgroundLarge = usePreloadImage(blueberriesSmallImage);

  usePreloadImage(typewriterImage);

  if (backgroundSmall.isLoading || backgroundLarge.isLoading) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{i18n.t("freeProductsCalorieDeficitCalculator:title")}</title>
        <meta
          property="og:title"
          content={i18n.t("freeProductsCalorieDeficitCalculator:sharingTitle")}
        />
        <meta
          name="description"
          content={i18n.t(
            "freeProductsCalorieDeficitCalculator:sharingDescription"
          )}
        />
        <meta
          property="og:description"
          content={i18n.t(
            "freeProductsCalorieDeficitCalculator:sharingDescription"
          )}
        />
        <meta property="og:image" content={calculatorPreviewImage} />
        <meta property="twitter:image" content={calculatorPreviewImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@noom" />
        <meta property="og:url" content={calorieCalculatorURL()} />
        <style>{`
          body {
            background-color: ${compassColors.offWhite};
          }
        `}</style>
      </Helmet>
      <FlexFrame css={{ justifyContent: "space-between" }}>
        <div>
          {header ? <Header /> : null}
          <MainColumn maxWidth={600} css={containerStyles}>
            <Spacer>{children}</Spacer>
          </MainColumn>
        </div>
        {footer ? (
          <Footer>
            <LandingFooter />
          </Footer>
        ) : null}
      </FlexFrame>
    </>
  );
}
